import moment, { Moment } from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import './date-selector.scss';
import { ReactComponent as DownIcon } from 'assets/icon/chevron-down.svg';
import { getYearsList, monthOptions } from 'helpers/utils';
import { useTranslation } from 'react-i18next';

interface IDatePickerProps {
  isFromDateRange?: boolean;
  startDate?: Moment;
  setStartDate?: (date: string) => void;
  showCurrentMonth?: boolean;
  isOnlyWeekDays?: boolean;
  weekday?: number;
  disablePastDays?: boolean;
  disableFutureDays?: boolean;
  top?: string;
  classname?: string;
  isMultiSelect?: true;
  dates?: Moment[];
  setDates?: (data: Moment[]) => void;
}

const DateSelector: React.FC<IDatePickerProps> = ({
  startDate,
  setStartDate = (data: string) => {},
  showCurrentMonth = true,
  disablePastDays = false,
  disableFutureDays = false,
  top,
  classname = '',
  isMultiSelect = false,
  dates = [],
  setDates = (data: Moment[]) => {},
}) => {
  const currentYearRef = useRef<HTMLDivElement>(null);
  const years = getYearsList(disablePastDays, disableFutureDays);
  const weekDays: string[] = ['Mon', 'Tue', 'Wed', 'Thr', 'Fri', 'Sat', 'Sun'];
  const [value, setValue] = useState<Moment>(
    showCurrentMonth ? moment().clone() : moment(startDate).clone(),
  );
  const [showMonthDropdown, setShowMonthDropdown] = useState<boolean>(false);
  const [showYearDropdown, setShowYearDropdown] = useState<boolean>(false);

  const [calendar, setCalendar] = useState<Moment[][]>([]);
  const startDay: Moment = value.clone().startOf('month').startOf('week');
  const endDay: Moment = value.clone().endOf('month').endOf('week');
  const currentDateRef = useRef<Moment>(moment());
  const { t } = useTranslation();

  useEffect(() => {
    const day: Moment = startDay.clone().subtract(1, 'day');
    const a: Moment[][] = [];
    while (day.isBefore(endDay, 'day')) {
      a.push(
        Array(7)
          .fill(0)
          .map(() => day.add(1, 'day').clone()),
      );
    }
    setCalendar(a);
  }, [value]);

  useEffect(() => {
    setValue(showCurrentMonth ? moment().clone() : moment(startDate).clone());
  }, [startDate]);

  const changeSelectedDate = (day: Moment) => {
    if (isMultiSelect) {
      const isDateSelected = dates.some(dateObj =>
        moment(dateObj)
          .set('hour', 0)
          .set('minute', 0)
          .set('second', 0)
          .set('millisecond', 0)
          .isSame(day, 'date'),
      );
      const availableDate = dates.filter(
        dateObj =>
          !moment(dateObj)
            .set('hour', 0)
            .set('minute', 0)
            .set('second', 0)
            .set('millisecond', 0)
            .isSame(day, 'date'),
      );
      setDates(isDateSelected ? availableDate : [...dates, day]);
    } else {
      setStartDate(moment(day).clone().toString());
    }
  };
  const currentYear = new Date().getFullYear();

  useEffect(() => {
    const currentYearElement = currentYearRef.current;
    if (currentYearElement) {
      currentYearElement.focus();
      currentYearElement.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    }
  }, [years]);
  const isDateSelected = (d: Moment) => {
    try {
      const isDateAvailable = dates.some(dateObj =>
        moment(dateObj)
          .set('hour', 0)
          .set('minute', 0)
          .set('second', 0)
          .set('millisecond', 0)
          .isSame(d, 'date'),
      );
      return isDateAvailable ? true : false;
    } catch (error) {}
  };
  return (
    <div
      className={`date-selector send-calendar py-5 px-6 ${classname}`}
      style={{ top: top ? top : '' }}
    >
      <div className="month-selector flex items-center justify-center mb-4 mx-3 gap-x-3">
        <div
          className="flex items-center relative"
          tabIndex={0}
          onBlur={e => {
            e.preventDefault();
            if (!e.currentTarget.contains(e.relatedTarget)) {
              setShowMonthDropdown(false);
            }
          }}
        >
          <div
            className="flex items-center justify-center bg-bg-50 px-4 py-2 rounded-full cursor-pointer w-[130px]"
            onClick={() => setShowMonthDropdown(!showMonthDropdown)}
          >
            <label className="text-sm text-primary font-bold pr-1 cursor-pointer flex-1">
              {t(`months.${value.format('MMMM')}`)}
            </label>
            <DownIcon height={16} width={16} color="#337B9E" />
          </div>
          {showMonthDropdown && (
            <div className="menu-wrapper absolute w-auto rounded-3 top-10 left-2 min-w-[100px] max-h-72 px-1 py-1 z-10 overflow-y-auto cursor-pointer">
              {monthOptions.map(month => (
                <div
                  key={`${month.name}-${month.value}`}
                  className={`flex items-center w-full`}
                  onClick={e => {
                    e.stopPropagation();
                    setValue(value.set('month', month.value).clone());
                    setShowMonthDropdown(false);
                  }}
                >
                  <label className="text-sm font-medium text-black1 px-3 py-1 hover:bg-grey8 w-full">
                    {t(`months.${month.name}`)}
                  </label>
                </div>
              ))}
            </div>
          )}
        </div>
        <div
          className="flex items-center relative"
          tabIndex={0}
          onBlur={e => {
            e.preventDefault();
            if (!e.currentTarget.contains(e.relatedTarget)) {
              setShowYearDropdown(false);
            }
          }}
        >
          <div
            className="flex items-center justify-center bg-bg-50 px-3 py-2 rounded-full"
            onClick={() => setShowYearDropdown(!showYearDropdown)}
          >
            <label className="text-sm text-primary font-bold pr-1">{value.format('YYYY')}</label>
            <DownIcon height={16} width={16} color="#337B9E" />
          </div>
          {showYearDropdown && (
            <div className="menu-wrapper absolute w-auto rounded-3 top-10 left-1 min-w-[70px] max-h-72 px-1 py-1 z-10 overflow-y-auto cursor-pointer">
              {years.map(y => (
                <div
                  key={`${y.name}-${y.value}`}
                  className={`flex items-center w-full`}
                  onClick={e => {
                    e.stopPropagation();
                    setValue(value.set('year', y.value).clone());
                    setShowYearDropdown(false);
                  }}
                  ref={y.value === currentYear ? currentYearRef : null}
                >
                  <label
                    className={`text-sm font-medium rounded-2 text-center text-black1 px-0.5 py-0.5 hover:bg-grey8 w-full ${
                      y.value === currentYear ? 'bg-primary text-white' : ''
                    }`}
                  >
                    {y.name}
                  </label>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      <div className="week-days">
        {weekDays.map(wd => (
          <div key={wd} className="week-day">
            <label className="font-bold text-sm">{t(`weekday.${wd}`)}</label>
          </div>
        ))}
      </div>
      {calendar.map((week, j) => (
        <div key={j} className="week">
          {week.map((day, i) => {
            return (
              <div
                key={i}
                className={`day regular-text cursor ${!value.isSame(day, 'month') && 'fade'}
                 ${
                   disablePastDays &&
                   !currentDateRef.current.isSame(day, 'date') &&
                   !currentDateRef.current.isBefore(day, 'date') &&
                   'fade'
                 }
                 ${
                   disableFutureDays &&
                   !currentDateRef.current.isSame(day, 'date') &&
                   !currentDateRef.current.isAfter(day, 'date') &&
                   'fade'
                 }`}
                onClick={e => {
                  if (
                    !disablePastDays ||
                    (disablePastDays && currentDateRef.current.isBefore(day, 'date')) ||
                    currentDateRef.current.isSame(day, 'date')
                  ) {
                    changeSelectedDate(day);
                  } else {
                    e.stopPropagation();
                  }
                }}
              >
                <div
                  className={`w-full h-full flex items-center justify-center cursor-pointer ${
                    ((!isMultiSelect && day.isSame(startDate, 'day')) ||
                      (isMultiSelect && isDateSelected(day))) &&
                    'selected-day'
                  }`}
                >
                  <label className="cursor-pointer">{day.format('D')}</label>
                </div>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
};

export default DateSelector;
